import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

/** KAN-314: a unit's live pass/fail result for display (mirrors backend shape). */
export interface UnitQuizResultView {
  quizId: string;
  quizTitle?: string;
  earnedPoints: number;
  totalPoints: number;
  passed: boolean;
  attempted: boolean;
  pendingManual?: boolean;
}
export interface UnitResultView {
  unitId: string;
  aggregatePercent: number;
  status: 'passed' | 'failed' | 'pending' | 'in_progress' | 'not_applicable';
  thresholdApplied: number | null;
  floorViolations: Array<{ quizId: string; quizTitle?: string; earnedPoints: number; minRequired: number }>;
  gateViolations: Array<{ quizId: string; quizTitle?: string }>;
  pending: boolean;
  /** VS4: this unit gates the next unit (learner must pass it to advance). */
  requirePassToAdvance?: boolean;
  quizzes: UnitQuizResultView[];
}
export interface CourseResultView {
  applicable: boolean;
  aggregatePercent: number;
  passed: boolean;
  pending: boolean;
  thresholdApplied: number | null;
}
export interface PassingResultsResponse {
  units: UnitResultView[];
  course: CourseResultView;
}

/**
 * Fetches the live layered-passing-policy results for a course. Self-view omits
 * userId; instructor view passes the learner's id. Failures degrade to an empty
 * result so the rest of the page still renders.
 */
@Injectable({ providedIn: 'root' })
export class PassingPolicyResultsService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getResults(courseId: string, userId?: string): Observable<PassingResultsResponse> {
    const path = userId
      ? `${this.apiUrl}/courses/${courseId}/students/${userId}/unit-results`
      : `${this.apiUrl}/courses/${courseId}/unit-results`;
    return this.http.get<any>(path).pipe(
      map((r) => (r && r.data ? r.data : r) as PassingResultsResponse),
      map((r) => ({ units: r?.units ?? [], course: r?.course ?? null }) as PassingResultsResponse),
      catchError(() => of({ units: [], course: null as any }))
    );
  }
}
